import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {ItemParams} from 'react-contexify';
import {contextMenu} from 'react-contexify';
import isEqual from 'react-fast-compare';

import {autorun, runInAction} from 'mobx';

import type {GoalApiModel} from '@yourcoach/shared/api/goal';

import {delay} from '@yourcoach/async-utils';
import {i18n} from '@yourcoach/i18n';
import CupIcon from '@yourcoach/shared/assets/icons/primary/Trophy.svg';
import {alert} from '@yourcoach/shared/components/alert';
import {logger} from '@yourcoach/shared/utils/logger';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';

import Button from '@src/components/Button';
import Loader from '@src/components/Loader/Loader';
import NoResultsHeader from '@src/components/NoResultsHeader';
import useIsVisible from '@src/hooks/useIsVisible';
import CRUGoalModal from '@src/modules/Goals/CRUGoalModal';
import GoalsListItem from '@src/modules/Goals/GoalsListItem';
import {ContextMenu} from '@src/v2/components/ContextMenu';

import type {TabProps, TabRef} from './useTabs';

import styles from './Goals.module.css';

const GOAL_CONTEXT_MENU_ID = 'goal_context_menu';

export type GoalT =
  | GoalApiModel
  | Pick<GoalApiModel, '_id' | 'description' | 'is_client_managed' | 'title'>;

const CRUProgramGoals = forwardRef<TabRef, TabProps>(
  ({goalsStore, isActive, program}, ref) => {
    useImperativeHandle(ref, () => ({
      getData,
    }));

    const [goals, setGoals] = useState<GoalT[]>([]);
    const [isFetching, setIsFetching] = useState(goalsStore!.isFetching);
    const [isLoaded, setIsLoaded] = useState(goalsStore!.isLoaded);

    const fetchGoalsDelay = useRef(delay(0));
    const goalToEdit = useRef<GoalT>(undefined);

    const shouldRenderStub = useMemo(
      () => isLoaded && !goals.length,
      [goals.length, isLoaded],
    );

    const [cruGoalModalIsOpen, showCRUGoalModal, hideCRUGoalModal] =
      useIsVisible();

    const getData = useCallback(() => ({}), []);

    const fetchGoals = useCallback(async () => {
      try {
        await goalsStore!.fetch();
      } catch (error) {
        logger.error(error);

        fetchGoalsDelay.current = delay(2000);

        await fetchGoalsDelay.current;

        await fetchGoals();
      }
    }, [goalsStore]);

    useEffect(() => {
      const dispose = autorun(() => {
        setGoals(goalsStore!.items.slice());
        setIsLoaded(goalsStore!.isLoaded);
        setIsFetching(goalsStore!.isFetching);
      });

      return dispose;
    }, [goalsStore]);

    useEffect(() => {
      if (isActive && !isLoaded) {
        fetchGoals();
      }

      return () => {
        fetchGoalsDelay.current.cancel();
      };
    }, [fetchGoals, isActive, isLoaded]);

    const onAddGoalButtonClick = useCallback(() => {
      showCRUGoalModal();
    }, [showCRUGoalModal]);

    const onCRUGoalSuccess = useCallback(
      (goal: GoalT) => {
        hideCRUGoalModal();

        if (goalToEdit.current) {
          goalsStore!.updateItem(goal as GoalApiModel, goal);
        } else {
          goalsStore!.addItem(goal as GoalApiModel);
        }
      },
      [goalsStore, hideCRUGoalModal],
    );

    const onClearButtonClick = useCallback(async () => {
      const isConfirmed = await alert.confirm();

      if (!isConfirmed) {
        return;
      }

      runInAction(() => {
        goalsStore!.items = [];
      });
    }, [goalsStore]);

    const onGoalMoreButtonClick = useCallback(
      (goal: GoalT, event: React.MouseEvent<Element, MouseEvent>) => {
        contextMenu.show({
          event,
          id: GOAL_CONTEXT_MENU_ID,
          props: {
            goal,
          },
        });
      },
      [],
    );

    const onDeleteGoalButtonClick = useCallback(
      ({props}: ItemParams<{goal: GoalT}>) => {
        if (props) {
          const {goal} = props;

          goalsStore!.removeItem(goal as GoalApiModel);
        }
      },
      [goalsStore],
    );

    const onUpdateGoalButtonClick = useCallback(
      ({props}: ItemParams<{goal: GoalT}>) => {
        if (props) {
          const {goal} = props;

          contextMenu.hideAll();

          goalToEdit.current = goal;

          showCRUGoalModal();
        }
      },
      [showCRUGoalModal],
    );

    const renderContextMenu = useCallback(
      () => (
        <ContextMenu.Menu animation="fade" id={GOAL_CONTEXT_MENU_ID}>
          <ContextMenu.Item onClick={onUpdateGoalButtonClick}>
            {i18n.t('shared:edit')}
          </ContextMenu.Item>
          <ContextMenu.Item onClick={onDeleteGoalButtonClick} variant="error">
            {i18n.t('shared:delete')}
          </ContextMenu.Item>
        </ContextMenu.Menu>
      ),
      [onDeleteGoalButtonClick, onUpdateGoalButtonClick],
    );

    const onGoalClick = useCallback(
      (item: GoalT) => {
        contextMenu.hideAll();

        goalToEdit.current = item;

        showCRUGoalModal();
      },
      [showCRUGoalModal],
    );

    const onCRUGoalModalClose = useCallback(() => {
      goalToEdit.current = undefined;
      hideCRUGoalModal();
    }, [hideCRUGoalModal]);

    return (
      <>
        <div className={styles.addButtonsContainer}>
          <Button
            data-test-id={TestID.create_program_goals.button.add_main_goal}
            onClick={onAddGoalButtonClick}
          >
            <CupIcon />
            {i18n.t('shared:CRUProgramGoals.add_goal_button')}
          </Button>
          {!shouldRenderStub ? (
            <Button className={styles.clearButton} onClick={onClearButtonClick}>
              {i18n.t('shared:CRUProgramGoals.clear_button')}
            </Button>
          ) : null}
        </div>
        {!isLoaded && isFetching ? (
          <div>
            <Loader />
          </div>
        ) : null}
        {shouldRenderStub ? (
          <div className={styles.noResultsContainer}>
            <NoResultsHeader
              icon={CupIcon}
              text={i18n.t('shared:CRUProgramGoals.no_results_label')}
            />
          </div>
        ) : (
          <div className={styles.list}>
            {goals.map((goal, i) => (
              <GoalsListItem
                className={styles.listItem}
                goal={goal as GoalApiModel}
                index={i}
                key={goal._id}
                onClick={onGoalClick}
                onMoreButtonClick={onGoalMoreButtonClick}
              />
            ))}
          </div>
        )}
        <CRUGoalModal
          goal={goalToEdit.current}
          isOpen={cruGoalModalIsOpen}
          onAfterClose={onCRUGoalModalClose}
          onSuccess={onCRUGoalSuccess}
          programId={program ? program._id : undefined}
        />
        {renderContextMenu()}
      </>
    );
  },
);

export default memo(CRUProgramGoals, isEqual);
